import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'
import { NodeFileManagerContext } from '@/components/studio/node-editor/file-manager-context'
import {
  admitImageSelection,
  attachmentFromUrl,
  type ImageAttachmentSource,
  type ImageInputCapability,
  type StoredImageAsset,
} from '@/lib/canvas/image-input-capability'
import type { NodeState, NodeAction } from './base-node-state'

interface UseBaseNodeImagesParams {
  id: string
  canvasId?: string
  state: NodeState
  dispatch: React.Dispatch<NodeAction>
  onUpload?: (file: File) => void
  onUploadMultiple?: (files: File[]) => void
  capability?: ImageInputCapability
}

interface UseBaseNodeImagesReturn {
  handleImageUpload: () => Promise<void>
  handleImageRemove: (imageUrl: string) => Promise<void>
  handleChooseFromFileManager: () => Promise<void>
  addPickedImages: (picked: Array<{ url: string; name?: string; contentType?: string; cloudUrl?: string; bytes?: number }>) => void
  browsingFiles: boolean
  closeFileBrowser: () => void
  isUploading: boolean
  isRemovingRef: React.MutableRefObject<boolean>
}

export function useBaseNodeImages({
  id,
  canvasId,
  state,
  dispatch,
  onUpload,
  onUploadMultiple,
  capability,
}: UseBaseNodeImagesParams): UseBaseNodeImagesReturn {
  // File upload hook for backend storage
  const { uploadFile, isUploading } = useCanvasFiles()
  const fileManager = useContext(NodeFileManagerContext)
  const [browsingFiles, setBrowsingFiles] = useState(false)

  // Lock to prevent simultaneous image removals
  const isRemovingRef = useRef(false)

  // BUG #5 fix: Track current images length in a ref to avoid stale closure
  const imagesLengthRef = useRef(state.images.length)
  const imagesRef = useRef(state.images)
  const rolesRef = useRef(state.imageRoles)
  const assetsRef = useRef(state.imageAssets)
  useEffect(() => {
    imagesLengthRef.current = state.images.length
    imagesRef.current = state.images
    rolesRef.current = state.imageRoles
    assetsRef.current = state.imageAssets
  }, [state.images, state.imageRoles, state.imageAssets])

  const rememberImages = useCallback((urls: string[], source: ImageAttachmentSource, details: Array<{ url: string; name?: string; contentType?: string; cloudUrl?: string; bytes?: number }> = []) => {
    const nextAssets: Record<string, StoredImageAsset> = { ...assetsRef.current }
    for (const detail of details) {
      if (!detail.url) continue
      nextAssets[detail.url] = {
        name: detail.name,
        contentType: detail.contentType,
        bytes: detail.bytes,
        cloudUrl: detail.cloudUrl,
      }
    }
    if (capability) {
      const current = imagesRef.current.map(url => attachmentFromUrl(url, 'upload', { ...nextAssets[url], role: rolesRef.current[url] }))
      const incoming = urls.map(url => attachmentFromUrl(url, source, { ...nextAssets[url], role: rolesRef.current[url] }))
      const admitted = admitImageSelection(capability, current, incoming)
      const kept = new Set(admitted.attachments.map(item => item.url))
      dispatch({ type: 'SET_IMAGES', payload: admitted.attachments.map(item => item.url) })
      dispatch({ type: 'SET_IMAGE_ASSETS', payload: Object.fromEntries(Object.entries(nextAssets).filter(([url]) => kept.has(url))) })
      return
    }
    const available = NODE_VISUAL_CONSTANTS.limits.maxImages - imagesLengthRef.current
    if (available <= 0) return
    dispatch({ type: 'ADD_IMAGES', payload: urls.slice(0, available) })
  }, [capability, dispatch])

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = (capability?.formats ?? ['image/png', 'image/jpeg', 'image/webp', 'image/gif']).join(',')
    input.multiple = capability ? capability.multiple : true
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      if (files.length === 0) return

      if (!canvasId) {
        console.error('Canvas ID is required for file upload')
        return
      }

      // Image nodes keep every selected file and explain a model mismatch.
      // Other nodes still use the historical fixed cap.
      const filesToUpload = capability
        ? files
        : files.slice(0, Math.max(0, NODE_VISUAL_CONSTANTS.limits.maxImages - imagesLengthRef.current))
      if (filesToUpload.length === 0) return

      // Show upload progress
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: { uploading: true, current: 0, total: filesToUpload.length } })

      try {
        // Parallel upload: Upload all files simultaneously
        // BUG #6 fix: Atomic counter so progress never jumps backwards
        let completedCount = 0
        const uploadPromises = filesToUpload.map(async (file) => {
          try {
            const uploadedFile = await uploadFile(canvasId, {
              file,
              nodeId: id,
            })

            // Update progress with monotonically increasing counter
            completedCount += 1
            dispatch({
              type: 'SET_UPLOAD_PROGRESS',
              payload: { uploading: true, current: completedCount, total: filesToUpload.length }
            })

            return uploadedFile?.publicUrl
              ? { url: uploadedFile.publicUrl, name: file.name, contentType: file.type || uploadedFile.contentType || undefined, bytes: file.size || uploadedFile.size || undefined }
              : null
          } catch (err) {
            console.error(`Failed to upload ${file.name}:`, err)
            return null
          }
        })

        // Wait for all uploads to complete
        const uploadResults = await Promise.all(uploadPromises)
        const uploaded = uploadResults.flatMap(item => item ? [item] : [])

        // Keep every successful image. A model limit is explained, not used to discard files.
        if (uploaded.length > 0) {
          rememberImages(uploaded.map(item => item.url), 'upload', uploaded)

          // Notify callbacks
          if (filesToUpload.length === 1 && filesToUpload[0]) {
            onUpload?.(filesToUpload[0])
          }
          if (filesToUpload.length > 0) {
            onUploadMultiple?.(filesToUpload)
          }
        }
      } catch (err) {
        // Silent error handling
      } finally {
        // Hide progress
        dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: { uploading: false, current: 0, total: 0 } })
      }
    }
    input.click()
  }, [onUpload, onUploadMultiple, canvasId, id, uploadFile, dispatch, capability, rememberImages])

  const addPickedImages = useCallback((picked: Array<{ url: string; name?: string; contentType?: string; cloudUrl?: string; bytes?: number }>) => {
    const urls = picked.map(item => item.url).filter(Boolean)
    if (urls.length === 0) return
    rememberImages(urls, 'file-manager', picked)
    setBrowsingFiles(false)
  }, [rememberImages])

  const handleChooseFromFileManager = useCallback(async () => {
    if (fileManager) {
      const picked = await fileManager.pickImage()
      if (picked) addPickedImages([picked])
      return
    }
    setBrowsingFiles(true)
  }, [addPickedImages, fileManager])

  const handleImageRemove = useCallback(async (imageUrl: string) => {
    // Prevent multiple simultaneous removals using ref lock
    if (isRemovingRef.current) return

    isRemovingRef.current = true
    dispatch({ type: 'SET_REMOVING_URL', payload: imageUrl })

    try {
      // Remove from state
      dispatch({ type: 'REMOVE_IMAGE', payload: imageUrl })
    } finally {
      // Clear removing state (hide spinner and unlock)
      dispatch({ type: 'SET_REMOVING_URL', payload: null })
      isRemovingRef.current = false
    }
  }, [dispatch])

  return {
    handleImageUpload,
    handleImageRemove,
    handleChooseFromFileManager,
    addPickedImages,
    browsingFiles,
    closeFileBrowser: () => setBrowsingFiles(false),
    isUploading,
    isRemovingRef,
  }
}
